import React, { useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { benefitsService } from '../services/benefits';
import { subscriptionService } from '../services/subscription';
import { benefitDiscountPct } from '../lib/utils';
import { AhorroCifra } from './ui/AhorroCifra';
import { VD, RADIO, SOMBRA } from '../lib/brand';

/*
  La calculadora de ahorro, en un solo lugar.

  Vive aquí y no dentro del landing porque se usa en dos sitios: la sección del
  landing y la pestaña de ahorro de la app. Iván pidió lo segundo —«si le doy
  clic a la calculadora, que no me mande al landing»— y copiar el bloque habría
  creado la segunda versión de algo que ya se nos duplicó cinco veces con la
  flip card.

  Ahora suma VARIAS tiendas (Iván, 30-ago-2026). Antes era un solo desplegable,
  y una tienda sola casi nunca cubre el costo del acceso: la pregunta real no es
  «cuánto me ahorro en Nike» sino «con todo lo que voy a comprar, ¿me conviene?».
  El molde es la herramienta que los closers ya usan en piso
  (`closer/SavingsCalculator`), que es exactamente esto con otro encabezado.

  Solo entran los beneficios con un porcentaje calculable. Un 2x1, un regalo o
  unos meses sin intereses no se pueden convertir a pesos y sumar sin inventar
  una cifra; esos siguen mostrándose en el carrusel de descuentos, que desde el
  arreglo de #30 sí los enseña todos.

  Cada pantalla pone su propio encabezado; esto es solo el cálculo.
*/

const RESPALDO = [
  { id: 'nike', name: 'Nike', pct: 20 },
  { id: 'adidas', name: 'Adidas', pct: 15 },
  { id: 'ishop', name: 'iShop', pct: 10 },
];

const pesos = (n) => `$${Math.round(n).toLocaleString('es-MX')}`;

export default function CalculadoraAhorro({ compacta = false, compararConAcceso = false }) {
  const [opciones, setOpciones] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [filas, setFilas] = useState([{ tiendaId: '', monto: '' }]);

  useEffect(() => {
    let vigente = true;

    Promise.all([
      benefitsService.getAll().catch(() => ({ data: [] })),
      compararConAcceso ? subscriptionService.getPlans().catch(() => null) : Promise.resolve(null),
    ]).then(([resBeneficios, resPlanes]) => {
      if (!vigente) return;

      const conPct = (resBeneficios?.data ?? [])
        .map((b) => ({ ...b, pct: benefitDiscountPct(b) }))
        .filter((b) => b.pct > 0);

      /* Varias tiendas traen el mismo beneficio cargado dos veces y en el
         desplegable se vería la opción repetida. Dos porcentajes distintos de
         una misma tienda sí son ofertas distintas y se conservan. */
      const vistos = new Set();
      const lista = conPct
        .filter((b) => {
          const clave = `${b.storeName}|${b.pct}`;
          if (vistos.has(clave)) return false;
          vistos.add(clave);
          return true;
        })
        .map((b) => ({ id: String(b.id), name: b.storeName || 'Tienda', pct: b.pct }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setOpciones(lista.length ? lista : RESPALDO);
      if (resPlanes?.success) setPlanes(resPlanes.data?.plans ?? []);
    });

    return () => { vigente = false; };
  }, [compararConAcceso]);

  const cambiarFila = (i, campo, valor) =>
    setFilas((f) => f.map((fila, j) => (j === i ? { ...fila, [campo]: valor } : fila)));
  const agregarFila = () => setFilas((f) => [...f, { tiendaId: '', monto: '' }]);
  const quitarFila = (i) => setFilas((f) => f.filter((_, j) => j !== i));

  const ahorroDe = (fila) => {
    const opcion = opciones.find((o) => o.id === fila.tiendaId);
    const gasto = parseFloat(String(fila.monto).replace(/,/g, '')) || 0;
    if (!opcion || gasto <= 0) return 0;
    return (gasto * opcion.pct) / 100;
  };

  const gastoTotal = filas.reduce((s, f) => s + (parseFloat(String(f.monto).replace(/,/g, '')) || 0), 0);
  const ahorroTotal = filas.reduce((s, f) => s + ahorroDe(f), 0);

  /* El anual es el plan que se busca cerrar; el mensual es el escalón de
     entrada, y sirve como cierre alterno cuando el ahorro todavía no llega. */
  const { anual, mensual } = useMemo(() => ({
    anual: planes.find((p) => p.interval === 'year'),
    mensual: planes.find((p) => p.interval === 'month'),
  }), [planes]);

  const precioAcceso = anual?.amount ?? 0;
  const comparar = compararConAcceso && precioAcceso > 0;
  const cubreAnual = comparar && ahorroTotal >= precioAcceso;
  const cubreMensual = comparar && !cubreAnual && mensual && ahorroTotal >= mensual.amount;

  const campo = {
    width: '100%', height: 52, borderRadius: RADIO.input, padding: '0 16px',
    background: VD.blanco, border: `1.5px solid ${VD.linea}`,
    fontSize: 15, color: VD.tinta, outline: 'none', boxSizing: 'border-box',
  };
  const etiqueta = { fontSize: 13, fontWeight: 600, color: VD.grafito, marginBottom: 8, display: 'block' };

  if (opciones.length === 0) return null;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filas.map((fila, i) => {
          const ahorro = ahorroDe(fila);
          return (
            <div
              key={i}
              style={{
                background: VD.blanco, borderRadius: RADIO.card,
                border: `1px solid ${VD.linea}`, padding: 16,
              }}
            >
              <label style={etiqueta} htmlFor={`calc-tienda-${i}`}>
                {i === 0 ? '¿Dónde vas a comprar?' : '¿Y también?'}
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <select
                  id={`calc-tienda-${i}`}
                  value={fila.tiendaId}
                  onChange={(e) => cambiarFila(i, 'tiendaId', e.target.value)}
                  style={{ ...campo, cursor: 'pointer', flex: 1 }}
                >
                  <option value="">Elige una tienda</option>
                  {opciones.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} — {o.pct}% adicional
                    </option>
                  ))}
                </select>
                {filas.length > 1 && (
                  <button
                    type="button"
                    onClick={() => quitarFila(i)}
                    aria-label={`Quitar la tienda ${i + 1}`}
                    style={{
                      width: 44, height: 44, borderRadius: RADIO.input, flexShrink: 0,
                      border: `1px solid ${VD.linea}`, background: VD.blanco, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={16} color={VD.pizarra} />
                  </button>
                )}
              </div>

              <label style={etiqueta} htmlFor={`calc-monto-${i}`}>¿Cuánto vas a gastar ahí?</label>
              <input
                id={`calc-monto-${i}`}
                type="number"
                inputMode="decimal"
                min="0"
                value={fila.monto}
                onChange={(e) => cambiarFila(i, 'monto', e.target.value)}
                placeholder="Ingresa el monto en pesos"
                style={campo}
              />

              {ahorro > 0 && (
                <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600, color: VD.tinta }}>
                  Aquí te ahorras {pesos(ahorro)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={agregarFila}
        style={{
          width: '100%', marginTop: 12, height: 50, borderRadius: RADIO.input,
          border: `1.5px dashed ${VD.linea}`, background: 'transparent', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          fontSize: 14.5, fontWeight: 600, color: VD.pizarra,
        }}
      >
        <Plus size={16} /> Agregar otra tienda
      </button>

      <div
        style={{
          marginTop: 16,
          background: VD.blanco,
          borderRadius: RADIO.card,
          padding: compacta ? '18px 16px' : '24px 20px',
          textAlign: 'center',
          border: `1px solid ${VD.linea}`,
          boxShadow: SOMBRA.md,
          opacity: ahorroTotal > 0 ? 1 : 0.45,
          transition: 'opacity .3s ease',
        }}
      >
        {ahorroTotal > 0 ? (
          <>
            <div style={{ fontSize: 14, color: VD.pizarra, marginBottom: 8 }}>Con vondi te ahorras</div>
            <AhorroCifra as="div" style={{ fontSize: compacta ? 'clamp(34px,11vw,46px)' : 'clamp(44px,14vw,60px)' }}>
              {pesos(ahorroTotal)}
            </AhorroCifra>
            <div style={{ fontSize: 13, color: VD.pizarra, marginTop: 8 }}>
              Sobre una compra de {pesos(gastoTotal)}
            </div>

            {comparar && (
              <div
                style={{
                  marginTop: 16, paddingTop: 16, borderTop: `1px solid ${VD.linea}`,
                  fontSize: 14.5, lineHeight: 1.5, fontWeight: 600, color: VD.tinta,
                }}
              >
                {cubreAnual
                  ? `Recuperas los ${pesos(precioAcceso)} del acceso anual con esta sola compra, y todavía te sobran ${pesos(ahorroTotal - precioAcceso)}.`
                  : cubreMensual
                    ? `Ya recuperas el plan mensual de ${pesos(mensual.amount)} con esta compra. Te faltan ${pesos(precioAcceso - ahorroTotal)} para cubrir el año completo.`
                    : `Te faltan ${pesos(precioAcceso - ahorroTotal)} de ahorro para recuperar el acceso anual. Agrega otra tienda.`}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 14, color: VD.pizarra }}>
            Elige una tienda y escribe cuánto vas a gastar para ver tu ahorro
          </div>
        )}
      </div>
    </div>
  );
}
